import { useRecentCanvases } from "@/api/canvas";
import {
  useRecentFileRecords,
  useWorkspaceFileRecords,
} from "@/api/fileRecords";
import {
  useRecentWorkspaceFlowSessions,
  useStarredWorkspaceFlowSessions,
  useWorkspaceFlowSessions,
  useWorkspaceStarredFlowSessions,
} from "@/api/flow_sessions";
import {
  useFavoriteCanvases,
  useFavoriteFileRecords,
} from "@/api/resourceTags";
import { useSkills } from "@/api/skills";
import { useWorkspaceCanvases } from "@/api/workspaces";
import {
  RESOURCE_TAG_FAVORITE,
  type Canvas,
  type FileRecord,
  type InboxItem,
} from "@cohesive/api-client";
import { useMemo } from "react";
import {
  HOME_BROWSER_LIMIT,
  type BrowserItem,
  type HomeTab,
} from "../utils/homeBrowserTypes";

export interface HomeTabData {
  items: BrowserItem[];
  /** True while nothing is displayable yet (all active queries pending). */
  isPending: boolean;
  /**
   * True when any active query failed. On merged tabs a single failed source
   * would otherwise leave a gap that reads as an empty account, so surface the
   * error path (the list only shows it when there are no items to display).
   */
  isError: boolean;
  refetch: () => void;
}

const canvasItems = (canvases: Canvas[]): BrowserItem[] =>
  canvases.map((data): BrowserItem => ({ kind: "canvas", data }));

// No folder rows on home — folder navigation lives on the workspace detail
// page. (The recents endpoint already excludes folders; the favorites and
// per-workspace lists do not.)
const fileItems = (files: FileRecord[]): BrowserItem[] =>
  files
    .filter((record) => !record.path.endsWith("/"))
    .map((data): BrowserItem => ({ kind: "file", data }));

const isFavorite = (item: { tags?: string[] }): boolean =>
  !!item.tags?.includes(RESOURCE_TAG_FAVORITE);

/**
 * Data for the active home tab, optionally scoped to a single workspace.
 *
 * Unscoped (`workspaceId` undefined) each tab maps to a small set of org-wide
 * queries; scoped, it maps to the equivalent per-workspace queries:
 *
 * - All      — canvases + files + chats, merged (sorted/capped by the caller).
 * - Files    — the files the user created, newest first.
 * - Canvases — recent canvases.
 * - Chats    — recent chats.
 * - Starred  — favorited canvases + files + starred chats, merged. Unscoped
 *              also includes skills; skills have no workspace, so the scoped
 *              tab omits them. Scoped favorites are derived by tag-filtering
 *              the workspace lists rather than narrowing the org-wide favorite
 *              queries, whose `workspace_id` is optional.
 *
 * Rules of hooks forbid conditional calls, so both query sets are always
 * declared and only `enabled` toggles between them. The workspace hooks
 * already self-gate on `!!workspaceId`, which composes with the tab gating.
 * Sources load independently and render progressively (a tab is "pending"
 * only while every one of its sources is still pending).
 */
export function useHomeBrowserData(
  tab: HomeTab,
  workspaceId?: string,
): HomeTabData {
  const isAll = tab === "all";
  const isRecents = tab === "recents";
  const isCanvases = tab === "canvases";
  const isChats = tab === "chats";
  const isStarred = tab === "starred";

  const scoped = !!workspaceId;

  // ----- org-wide (unscoped home) -----
  const recentCanvases = useRecentCanvases(HOME_BROWSER_LIMIT, {
    enabled: !scoped && (isAll || isCanvases),
  });
  const recentFiles = useRecentFileRecords(HOME_BROWSER_LIMIT, {
    enabled: !scoped && (isAll || isRecents),
  });
  const recentChats = useRecentWorkspaceFlowSessions(HOME_BROWSER_LIMIT, {
    enabled: !scoped && (isAll || isChats),
  });
  const favoriteCanvases = useFavoriteCanvases({
    enabled: !scoped && isStarred,
  });
  const favoriteFiles = useFavoriteFileRecords({
    enabled: !scoped && isStarred,
  });
  const skills = useSkills({ enabled: !scoped && isStarred });
  const starredChats = useStarredWorkspaceFlowSessions({
    enabled: !scoped && isStarred,
  });

  // ----- workspace-scoped. Canvases/files are also enabled on Starred,
  // because the scoped favorites are a tag-filter over these same lists.
  const wsCanvases = useWorkspaceCanvases(workspaceId, {
    enabled: scoped && (isAll || isCanvases || isStarred),
  });
  const wsFiles = useWorkspaceFileRecords(workspaceId, {
    enabled: scoped && (isAll || isRecents || isStarred),
  });
  const wsChats = useWorkspaceFlowSessions(workspaceId, {
    enabled: scoped && (isAll || isChats),
  });
  const wsStarredChats = useWorkspaceStarredFlowSessions(workspaceId, {
    enabled: scoped && isStarred,
  });

  // The query feeding each kind on the active tab.
  const canvasQuery = scoped ? wsCanvases : recentCanvases;
  const fileQuery = scoped ? wsFiles : recentFiles;
  const chatQuery = scoped ? wsChats : recentChats;
  const starredChatQuery = scoped ? wsStarredChats : starredChats;

  const items = useMemo<BrowserItem[]>(() => {
    // Mirrors useWorkspaceBrowserData's guard: the scoped chat lists are
    // server-filtered, but a chat moved to another workspace lingers in this
    // cache until refetch — drop rows that no longer belong. (Strict on the
    // optional flow_session relation, same trade-off as the old detail page.)
    const chatItems = (chats: InboxItem[]): BrowserItem[] =>
      chats
        .filter((c) => !scoped || c.flow_session?.workspace_id === workspaceId)
        .map((data): BrowserItem => ({ kind: "chat", data }));

    switch (tab) {
      case "all":
        return [
          ...canvasItems(canvasQuery.data ?? []),
          ...fileItems(fileQuery.data ?? []),
          ...chatItems(chatQuery.data ?? []),
        ];
      case "recents":
        return fileItems(fileQuery.data ?? []);
      case "canvases":
        return canvasItems(canvasQuery.data ?? []);
      case "chats":
        return chatItems(chatQuery.data ?? []);
      case "starred":
        return [
          ...canvasItems(
            scoped
              ? (wsCanvases.data ?? []).filter(isFavorite)
              : (favoriteCanvases.data ?? []),
          ),
          ...fileItems(
            scoped
              ? (wsFiles.data ?? []).filter(isFavorite)
              : (favoriteFiles.data ?? []),
          ),
          // Skills aren't scoped to a workspace, so they only belong on home.
          ...(scoped
            ? []
            : (skills.data ?? [])
                .filter(isFavorite)
                .map((data): BrowserItem => ({ kind: "skill", data }))),
          // Render-filter on is_starred so an optimistic unstar drops the row.
          ...chatItems(
            (starredChatQuery.data ?? []).filter((chat) => chat.is_starred),
          ),
        ];
    }
  }, [
    tab,
    scoped,
    workspaceId,
    canvasQuery.data,
    fileQuery.data,
    chatQuery.data,
    starredChatQuery.data,
    wsCanvases.data,
    wsFiles.data,
    favoriteCanvases.data,
    favoriteFiles.data,
    skills.data,
  ]);

  // The queries feeding the active tab, for pending/error/refetch aggregation.
  const activeQueries = isAll
    ? [canvasQuery, fileQuery, chatQuery]
    : isRecents
      ? [fileQuery]
      : isCanvases
        ? [canvasQuery]
        : isChats
          ? [chatQuery]
          : scoped
            ? [wsCanvases, wsFiles, wsStarredChats]
            : [favoriteCanvases, favoriteFiles, skills, starredChats];

  return {
    items,
    isPending: activeQueries.every((q) => q.isPending),
    isError: activeQueries.some((q) => q.isError),
    refetch: () => activeQueries.forEach((q) => void q.refetch()),
  };
}
